// SAB-565 pt 2: unified wallet activity mapping. Turns a raw wallet_transactions
// row into a display model for Recent activity + /wallet/history, per the wallet-home
// handoff row table + the decided type mapping. Never renders a raw enum/system string.
//
// STATUS is read faithfully from wallet_transactions.status — the same field the
// NFLFundsSection engine renders — so the two surfaces cannot disagree. A row is
// PENDING (gold Clock) until the processor terminally confirms it: only completed/
// settled/succeeded count as done; failed/voided/returned/refunded (and reversals)
// are red; anything else in-flight is pending. NEVER default an unknown status to
// completed (a withdrawal is pending until confirmed).
//
// Money: wallet_transactions.amount is stored POSITIVE for every type; the sign is
// derived from the type (credit vs debit), not the stored value.

export interface WalletTxnRow {
  id: string;
  type: string;
  amount: number | string;
  status: string;
  metadata: Record<string, any> | null;
  description?: string | null;
  reference_id?: string | null;
  league_id?: string | null;
  created_at: string;
}

export type ActivityIcon = 'ArrowUp' | 'ArrowDown' | 'Clock' | 'Briefcase' | 'BarChart3' | 'XCircle';
export type ToneKey = 'success' | 'gold' | 'neutral' | 'damage' | 'fg';

export interface ActivityView {
  id: string;
  title: string;         // "Deposit · Chase ••1234"  (source omitted when unknown)
  meta: string;          // "Jul 22 · Completed"
  amountText: string;    // "+$200.00" / "−$50.00"   (U+2212 minus for debits)
  icon: ActivityIcon;
  tone: Exclude<ToneKey, 'fg'>; // icon-tile tone
  amountTone: ToneKey;   // amount text color
}

const CREDIT_TYPES = new Set(['deposit', 'prize_payout', 'bonus_payout']);
// Terminal states. Everything NOT terminal-ok and NOT failed is treated as in-flight (pending).
const DONE = new Set(['completed', 'settled', 'succeeded']);
const FAILED = new Set(['failed', 'voided', 'returned', 'refunded', 'cancelled', 'canceled', 'expired', 'terminally_failed']);

const TYPE_LABEL: Record<string, string> = {
  deposit: 'Deposit',
  withdrawal: 'Withdrawal',
  league_fee: 'League entry',
  sabotage_purchase: 'Card purchase',
  prize_payout: 'Prize payout',
  bonus_payout: 'Prize payout',
  reversal: 'Reversed',
};
const STATUS_LABEL: Record<string, string> = {
  completed: 'Completed', settled: 'Completed', succeeded: 'Completed',
  pending: 'Pending', processing: 'Processing', submitted: 'Processing', initiated: 'Processing', created: 'Pending',
  failed: 'Failed', voided: 'Reversed', returned: 'Returned', refunded: 'Refunded',
  cancelled: 'Canceled', canceled: 'Canceled', expired: 'Expired', terminally_failed: 'Failed',
};

const toCents = (a: number | string) => Math.round(Number(a || 0) * 100);
const fmt = (cents: number) => (Math.abs(cents) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const titleCase = (s: string) => (s || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

// Fix 3: strip internal/system qualifiers from an institution label, e.g.
// "Aerosync Bank (oAuth)" -> "Aerosync Bank". Never render raw system strings.
export const cleanBankLabel = (name?: string | null): string =>
  (name || '').replace(/\s*\([^)]*\)/g, '').replace(/\s{2,}/g, ' ').trim() || 'Bank';

// bankMap: linked_bank_accounts.id -> "Chase ••1234" (already sanitized by caller).
// leagueMap: league id -> name.
export function mapActivity(
  row: WalletTxnRow,
  bankMap: Record<string, string> = {},
  leagueMap: Record<string, string> = {},
): ActivityView {
  const m = row.metadata || {};
  const isCredit = CREDIT_TYPES.has(row.type);
  const failed = FAILED.has(row.status) || row.type === 'reversal';
  const done = DONE.has(row.status);
  const pending = !failed && !done; // in-flight until terminally confirmed

  // ── source string (never a raw enum/system value) ──
  let source: string | null = null;
  if (row.type === 'deposit') {
    const method = m.provider === 'soap' ? 'soap' : (m.payment_method as string | undefined);
    if (method === 'soap') source = 'Debit card';
    else if (method === 'ach') source = (m.bank_account_id && bankMap[m.bank_account_id]) || 'Bank';
    else if (method === 'paypal') source = 'PayPal';
    else if (method === 'venmo') source = 'Venmo';
  } else if (row.type === 'withdrawal') {
    source = (m.bank_account_id && bankMap[m.bank_account_id]) || 'Bank';
  } else if (row.type === 'league_fee') {
    source = m.league_name || (row.league_id && leagueMap[row.league_id]) || 'League';
  } else if (row.type === 'prize_payout' || row.type === 'bonus_payout') {
    source = m.week ? `Week ${m.week}` : null;
  }

  const label = TYPE_LABEL[row.type] || titleCase(row.type);
  const title = source ? `${label} · ${source}` : label;

  const d = new Date(row.created_at);
  const dateStr = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  const meta = `${dateStr} · ${STATUS_LABEL[row.status] || titleCase(row.status)}`;

  const cents = toCents(row.amount);
  const amountText = `${isCredit ? '+' : '−'}$${fmt(cents)}`; // U+2212 minus for debits

  // ── icon + tones — status wins: pending (any type) = gold Clock; failed = red ──
  let icon: ActivityIcon;
  let tone: ActivityView['tone'];
  let amountTone: ToneKey;
  if (failed) {
    icon = 'XCircle'; tone = 'damage'; amountTone = 'damage';
  } else if (pending) {
    icon = 'Clock'; tone = 'gold'; amountTone = 'gold';
  } else if (row.type === 'deposit') {
    icon = 'ArrowUp'; tone = 'success'; amountTone = 'success';
  } else if (row.type === 'prize_payout' || row.type === 'bonus_payout') {
    icon = 'BarChart3'; tone = 'success'; amountTone = 'success';
  } else if (row.type === 'withdrawal') {
    icon = 'ArrowDown'; tone = 'neutral'; amountTone = 'fg';
  } else {
    icon = 'Briefcase'; tone = 'neutral'; amountTone = 'fg'; // league_fee, sabotage_purchase, other debits
  }

  return { id: row.id, title, meta, amountText, icon, tone, amountTone };
}
